import { loadStitchedColors, navigateToStitchedColor } from '@theme/stitched-product-colors';

class ShopTheLookAddToCart extends HTMLElement {
  constructor() {
    super();
    this._variantsCache = new WeakMap();
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.initializeForms();
    this.addEvents();
    loadStitchedColors(this);
  }

  /**
   * @description Add event listeners
   * @returns {void}
   */
  addEvents() {
    this.addEventListener('submit', () => { this._awaitingCartAdd = true; }, true);
    this.addEventListener('cart:error', () => { this._awaitingCartAdd = false; });
    this.addEventListener('cart:update', (event) => {
      if (!this._awaitingCartAdd || event.detail?.data?.source !== 'product-form-component') return;
      this._awaitingCartAdd = false;
      if (!event.detail?.data?.didError) {
        this.closest('sdg-modal')?.close({ restoreFocus: false });

      }
    });
    this.addEventListener("change", (e) => {
      const target = e?.target;
      if (!(target instanceof Element)) return;

      if (target instanceof HTMLSelectElement && target.selectedOptions[0]?.dataset.stitchedProductUrl) {
        navigateToStitchedColor(this, target.selectedOptions[0]);
        return;
      }

      const scope = this.getScope(target);
      const form = this.getForm(scope);
      if (!form) return;

      this.syncFormState(scope, form, target);
    });
  }

  /**
   * @param {Element} target
   * @param {string | undefined} imageUrl
   */
  updateImageFromUrl(target, imageUrl) {
    if (!imageUrl) return;
    const item = target.closest(".stl-item, .complete-the-look__item, [data-cross-sell-card]");
    const thumbnail = item?.closest(".stl-modal--single")?.querySelector(".stl-single-image") ||
      item?.querySelector(".js-shop-the-look-product-thumbnail, slideshow-slide[aria-hidden='false'] img, .cart-recommendation-card__image");
    if (!(thumbnail instanceof HTMLImageElement)) return;

    const sizedUrl = new URL(imageUrl, window.location.origin);
    sizedUrl.searchParams.set("width", thumbnail.classList.contains("stl-single-image") ? "800" : "372");
    if (thumbnail.src !== sizedUrl.href) {
      thumbnail.src = sizedUrl.href;
      thumbnail.removeAttribute("srcset");
    }
  }

  /**
   * @param {Element} target
   * @param {string | undefined} priceHtml
   */
  updatePrice(target, priceHtml) {
    if (!priceHtml) return;
    const item = target.closest(".stl-item, .complete-the-look__item, [data-cross-sell-card]");
    const priceContainer = item?.querySelector('[data-stl-price], product-price [ref="priceContainer"], .cart-recommendation-card__price');
    if (!priceContainer) return;

    priceContainer.innerHTML = priceHtml;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {Array<Record<string, any>>}
   */
  getVariants(form) {
    const cached = this._variantsCache.get(form);
    if (cached) return cached;

    const variantsScript = form.querySelector("script[data-stl-variants]");
    if (!(variantsScript instanceof HTMLScriptElement) || !variantsScript.textContent) {
      return [];
    }

    try {
      const parsed = JSON.parse(variantsScript.textContent);
      const variants = Array.isArray(parsed) ? parsed : [];
      this._variantsCache.set(form, variants);
      return variants;
    } catch (_error) {
      return [];
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {HTMLInputElement | null}
   */
  getVariantIdInput(form) {
    const variantIdInput =
      form.querySelector('[ref="variantId"]') || form.querySelector('input[name="id"]');
    return variantIdInput instanceof HTMLInputElement ? variantIdInput : null;
  }

  /**
   * @returns {{ addToCart: string, soldOut: string, unavailable: string }}
   */
  getLabels() {
    return {
      addToCart: this.dataset.labelAddToCart || "Add to cart",
      soldOut: this.dataset.labelSoldOut || "Sold out",
      unavailable: this.dataset.labelUnavailable || "Unavailable",
    };
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} available
   * @param {string} text
   */
  updateButtonState(form, available, text) {
    const addToCartButton = form.querySelector('[ref="addToCartButton"]');
    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    addToCartButton.disabled = !available;

    const textContainer = addToCartButton.querySelector(".add-to-cart-text__content");
    if (textContainer) {
      textContainer.textContent = text;
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} variantId
   */
  updateVariantId(form, variantId) {
    const variantIdInput = this.getVariantIdInput(form);
    if (!variantIdInput) return;
    variantIdInput.value = variantId;
    const card = form.closest('[data-cross-sell-card]');
    const productUrl = form.closest('product-form-component')?.dataset.productUrl;
    if (card && productUrl) {
      const url = new URL(productUrl, window.location.origin);
      if (variantId) url.searchParams.set('variant', variantId);
      for (const link of card.querySelectorAll('a[href]')) {
        if (new URL(link.href).pathname === url.pathname) link.href = url.href;
      }
    }
  }

  /**
   * @param {Element} target
   * @returns {HTMLElement}
   */
  getScope(target) {
    const scope = target.closest("shop-the-look-add-to-cart");
    if (scope instanceof HTMLElement) return scope;
    return this;
  }

  /**
   * @param {Element | HTMLElement} scope
   * @returns {HTMLFormElement | null}
   */
  getForm(scope) {
    const form = scope.querySelector('form[data-type="add-to-cart-form"]');
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {HTMLFormElement} form
   * @param {Array<Record<string, any>>} variants
   */
  syncSelect(select, form, variants) {
    const option = select.options[select.selectedIndex];
    const labels = this.getLabels();

    if (!option) {
      this.updateVariantId(form, "");
      this.updateButtonState(form, false, labels.unavailable);
      return;
    }

    this.updateVariantId(form, select.value || "");

    const variant = variants.find((v) => String(v.id) === String(select.value));
    const imageUrl = option.dataset.variantImageUrl || variant?.featured_image?.src;
    this.updateImageFromUrl(select, imageUrl);
    this.updatePrice(select, option.dataset.variantPrice);
    const isAvailable =
      option.disabled === false &&
      (option.dataset.variantAvailable ? option.dataset.variantAvailable === "true" : Boolean(variant?.available));
    const buttonText = isAvailable ? labels.addToCart : labels.soldOut;
    this.updateButtonState(form, isAvailable, buttonText);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {Array<Record<string, any>>} variants
   */
  syncSingleVariant(form, variants) {
    const labels = this.getLabels();
    const variantIdInput = this.getVariantIdInput(form);
    const fallbackVariant = variants[0] || null;
    const selectedVariant =
      (variantIdInput?.value
        ? variants.find((variant) => String(variant.id) === String(variantIdInput.value))
        : null) || fallbackVariant;

    if (!selectedVariant?.id) {
      this.updateVariantId(form, "");
      this.updateButtonState(form, false, labels.unavailable);
      return;
    }

    this.updateVariantId(form, String(selectedVariant.id));
    this.updateImageFromUrl(form, selectedVariant.featured_image?.src);
    this.updateButtonState(
      form,
      Boolean(selectedVariant.available),
      selectedVariant.available ? labels.addToCart : labels.soldOut
    );
  }

  /**
   * @param {Element | HTMLElement} scope
   * @param {HTMLFormElement} form
   * @param {Array<Record<string, any>>} variants
   * @param {Element | null} changedTarget
   */
  syncSplitOptions(scope, form, variants) {
    const controls = Array.from(scope.querySelectorAll('[data-stl-option-select], [data-stl-option-swatch]:checked'));
    const selectedOptions = [];
    for (const control of controls) selectedOptions[Number(control.dataset.optionIndex)] = control.value;
    const selects = controls.filter(control => control.tagName === 'SELECT');
    for (const group of scope.querySelectorAll('[data-stl-option-group]')) {
      group.querySelector('[data-selected-color]').textContent = selectedOptions[Number(group.dataset.optionIndex)];
    }
    const labels = this.getLabels();

    for (const select of selects) {
      const index = Number(select.dataset.optionIndex);
      for (const option of select.options) {
        if (option.dataset.stitchedProductUrl) continue;
        const available = variants.some((variant) =>
          variant.available && variant.options.every((value, position) =>
            position === index ? value === option.value : position > index || value === selectedOptions[position]
          )
        );
        option.dataset.optionAvailable = String(available);
        option.textContent = `${option.dataset.label}${available ? '' : ` — ${labels.soldOut}`}`.toUpperCase();
      }
    }

    const selectedVariant = variants.find((variant) =>
      variant.options.length === selectedOptions.length &&
      variant.options.every((value, index) => value === selectedOptions[index])
    );
    this.updateVariantId(form, selectedVariant?.id ? String(selectedVariant.id) : '');
    this.updateButtonState(form, Boolean(selectedVariant?.available),
      selectedVariant ? (selectedVariant.available ? labels.addToCart : labels.soldOut) : labels.unavailable);
    const imageVariant = selectedVariant || variants.find(variant =>
      controls.filter(control => control.type === 'radio').every(control =>
        variant.options[Number(control.dataset.optionIndex)] === control.value));
    this.updateImageFromUrl(form, imageVariant?.featured_image?.src);
    const prices = form.querySelector('script[data-stl-prices]');
    if (prices?.textContent && selectedVariant) {
      this.updatePrice(form, JSON.parse(prices.textContent)[selectedVariant.id]);
    }
  }

  /**
   * @param {Element | HTMLElement} scope
   * @param {HTMLFormElement} form
   */
  syncFormState(scope, form, changedTarget = null) {
    const variants = this.getVariants(form);
    if (!variants.length) return;

    const optionSelect = scope.querySelector("[data-stl-option-select], [data-stl-option-swatch]");
    if (optionSelect) {
      this.syncSplitOptions(scope, form, variants, changedTarget);
      return;
    }

    const select = scope.querySelector("[data-stl-variant-select]");
    if (select instanceof HTMLSelectElement) {
      this.syncSelect(select, form, variants);
      return;
    }

    this.syncSingleVariant(form, variants);
  }

  initializeForms() {
    const form = this.getForm(this);
    if (form) this.syncFormState(this, form);
  }

  refreshVariants() {
    const form = this.getForm(this);
    if (form) this._variantsCache.delete(form);
    this.initializeForms();
    loadStitchedColors(this);
  }
}

// Prevent double registration
if (!customElements.get("shop-the-look-add-to-cart")) {
  customElements.define("shop-the-look-add-to-cart", ShopTheLookAddToCart);
}
